import React from 'react';
import { Card, Row, Col, Divider, Button, Space, Typography } from 'antd';
import { useI18n } from '../hooks/useI18n';
import { useSettings } from '../contexts/SettingsContext';
import TimeDisplay from './TimeDisplay';
import { SUPPORTED_LANGUAGES, SUPPORTED_TIMEZONES } from '../types/settings';

const { Title, Text } = Typography;

const InternationalizationTest: React.FC = () => {
  const { t, currentLanguage, setCurrentLanguage } = useI18n();
  const { settings, updateSettings } = useSettings();

  // 测试时间
  const testUTCTime = '2024-09-08 12:30:45';

  const handleLanguageChange = (language: string) => {
    setCurrentLanguage(language);
  };

  const handleTimezoneChange = async (timezone: string) => {
    await updateSettings({ timezone });
  };

  return (
    <div style={{ padding: '20px' }}>
      <Title level={2}>{t('settings.title')} - 测试页面</Title>

      {/* 语言测试 */}
      <Card title="🌍 语言测试" style={{ marginBottom: '20px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>当前语言: {currentLanguage}</Text>
            <br />
            <Text>翻译测试: {t('common.save')}</Text>
            <br />
            <Text>参数测试: {t('time.minutesAgo', { count: 15 })}</Text>
          </Col>
          <Col span={12}>
            <Text strong>切换语言:</Text>
            <br />
            <Space wrap>
              {SUPPORTED_LANGUAGES.map(lang => (
                <Button
                  key={lang.code}
                  type={currentLanguage === lang.code ? 'primary' : 'default'}
                  onClick={() => handleLanguageChange(lang.code)}
                  size="small"
                >
                  {lang.flag} {lang.name}
                </Button>
              ))}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 时区测试 */}
      <Card title="🕐 时区测试" style={{ marginBottom: '20px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>当前时区: {settings.timezone}</Text>
            <br />
            <Text>UTC时间: {testUTCTime}</Text>
            <br />
            <Text>转换后时间: </Text>
            <TimeDisplay utcTime={testUTCTime} format="datetime" showTimezone />
            <br />
            <Text>相对时间: </Text>
            <TimeDisplay utcTime={testUTCTime} format="relative" />
          </Col>
          <Col span={12}>
            <Text strong>切换时区:</Text>
            <br />
            <Space wrap>
              {SUPPORTED_TIMEZONES.slice(0, 4).map(tz => (
                <Button
                  key={tz.code}
                  type={settings.timezone === tz.code ? 'primary' : 'default'}
                  onClick={() => handleTimezoneChange(tz.code)}
                  size="small"
                >
                  {tz.name}
                </Button>
              ))}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 时间格式测试 */}
      <Card title="📅 时间格式测试" style={{ marginBottom: '20px' }}>
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>日期格式:</Text>
            <br />
            <TimeDisplay utcTime={testUTCTime} format="date" />
          </Col>
          <Col span={8}>
            <Text strong>时间格式:</Text>
            <br />
            <TimeDisplay utcTime={testUTCTime} format="time" />
          </Col>
          <Col span={8}>
            <Text strong>完整格式:</Text>
            <br />
            <TimeDisplay utcTime={testUTCTime} format="datetime" />
          </Col>
        </Row>
        <Divider />
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>带时区显示:</Text>
            <br />
            <TimeDisplay utcTime={testUTCTime} format="datetime" showTimezone />
          </Col>
          <Col span={12}>
            <Text strong>相对时间:</Text>
            <br />
            <TimeDisplay utcTime={testUTCTime} format="relative" />
          </Col>
        </Row>
      </Card>

      {/* 设置状态 */}
      <Card title="⚙️ 设置状态" style={{ marginBottom: '20px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>当前设置:</Text>
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
              {JSON.stringify(settings, null, 2)}
            </pre>
          </Col>
          <Col span={12}>
            <Text strong>本地存储:</Text>
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
              {JSON.stringify({
                'user-settings': localStorage.getItem('user-settings'),
                'user-language': localStorage.getItem('user-language'),
                'user-timezone': localStorage.getItem('user-timezone'),
              }, null, 2)}
            </pre>
          </Col>
        </Row>
      </Card>

      {/* 功能说明 */}
      <Card title="📖 功能说明">
        <ul>
          <li><strong>多语言支持:</strong> 实时切换界面语言，支持5种语言</li>
          <li><strong>时区转换:</strong> 自动将UTC时间转换为用户时区</li>
          <li><strong>时间格式:</strong> 支持多种时间显示格式</li>
          <li><strong>相对时间:</strong> 显示"刚刚"、"X分钟前"等</li>
          <li><strong>设置持久化:</strong> 设置自动保存到本地存储</li>
          <li><strong>实时更新:</strong> 设置更改后立即生效</li>
        </ul>
      </Card>
    </div>
  );
};

export default InternationalizationTest;
